import { useState, type CSSProperties, type ReactNode } from "react";
import { Field } from "@design-system/Field/Field";
import {
    TagsInput,
    type TagsInputProps,
    type TagsInputSize,
} from "@design-system/TagsInput/TagsInput";
import { VariantGrid, VariantItem } from "./playgroundControls";

const sizes: readonly TagsInputSize[] = ["lg", "xl"];
const twoValues = ["Payroll", "Invoices"];
const wrappedValues = [
    "ana@kick.co",
    "ben@kick.co",
    "chloe@kick.co",
    "dmitri@kick.co",
    "esther@kick.co",
    "farid@kick.co",
    "grace@kick.co",
];
const longValue =
    "Monthly contractor invoice for the accounting and bookkeeping retainer";

const stageStyle: CSSProperties = { width: 320 };

/** Fixed-width box so every stage wraps at the same width. */
const Stage = ({ children }: { children: ReactNode }) => (
    <div style={stageStyle}>{children}</div>
);

/** A TagsInput that owns its values, so typing and removing stay live. */
const LiveTagsInput = ({
    initialValues,
    ...props
}: Omit<TagsInputProps, "value" | "onValueChange"> & {
    initialValues: readonly string[];
}) => {
    const [values, setValues] = useState(initialValues);

    return <TagsInput {...props} value={values} onValueChange={setValues} />;
};

/** Sizes, content cases and every chrome state, each stage live. */
export const TagsInputMatrix = () => (
    <>
        <VariantGrid title="Sizes" columns="wide">
            {sizes.map((size) => (
                <VariantItem key={size} label={`size="${size}"`}>
                    <Stage>
                        <LiveTagsInput
                            aria-label="Tags"
                            size={size}
                            initialValues={twoValues}
                        />
                    </Stage>
                </VariantItem>
            ))}
        </VariantGrid>
        <VariantGrid title="Content (xl)" columns="wide">
            <VariantItem label="empty · placeholder">
                <Stage>
                    <LiveTagsInput
                        aria-label="Tags"
                        initialValues={[]}
                        placeholder="Type or paste values"
                    />
                </Stage>
            </VariantItem>
            <VariantItem label="1 chip">
                <Stage>
                    <LiveTagsInput
                        aria-label="Tags"
                        initialValues={["Payroll"]}
                    />
                </Stage>
            </VariantItem>
            <VariantItem label="wrapped · 3+ rows">
                <Stage>
                    <LiveTagsInput
                        aria-label="Tags"
                        initialValues={wrappedValues}
                    />
                </Stage>
            </VariantItem>
            <VariantItem label="long label · ellipsis + tooltip">
                <Stage>
                    <LiveTagsInput
                        aria-label="Tags"
                        initialValues={[longValue]}
                    />
                </Stage>
            </VariantItem>
        </VariantGrid>
        <VariantGrid title="States (xl)" columns="wide">
            <VariantItem label='data-forced-state="hover"'>
                <Stage>
                    <LiveTagsInput
                        aria-label="Tags"
                        initialValues={twoValues}
                        data-forced-state="hover"
                    />
                </Stage>
            </VariantItem>
            <VariantItem label='data-forced-state="focus"'>
                <Stage>
                    <LiveTagsInput
                        aria-label="Tags"
                        initialValues={twoValues}
                        data-forced-state="focus"
                    />
                </Stage>
            </VariantItem>
            <VariantItem label="invalid · Field.Root invalid">
                <Stage>
                    <Field.Root invalid>
                        <LiveTagsInput
                            aria-label="Tags"
                            initialValues={twoValues}
                            placeholder="Add emails"
                        />
                    </Field.Root>
                </Stage>
            </VariantItem>
            <VariantItem label='invalid · data-forced-state="hover"'>
                <Stage>
                    <Field.Root invalid>
                        <LiveTagsInput
                            aria-label="Tags"
                            initialValues={twoValues}
                            placeholder="Add emails"
                            data-forced-state="hover"
                        />
                    </Field.Root>
                </Stage>
            </VariantItem>
            <VariantItem label="Field · label + error">
                <Stage>
                    <Field
                        label="Email addresses"
                        error="Enter a valid email address"
                    >
                        <LiveTagsInput
                            initialValues={["ana@kick.co"]}
                            placeholder="Add emails"
                        />
                    </Field>
                </Stage>
            </VariantItem>
        </VariantGrid>
    </>
);
